import React, { useEffect, useState } from 'react';
import { DrillFileDetails, Tag } from "../typings";
import { Row } from "../components/Flex";
import { fetchDrill, updateDrill } from "./drills-api";
import { useParams } from "react-router-dom";
import EditableInput from "../components/EditableInput";
import { PrimaryButton } from "../components/buttons/Buttons";
import { fetchTags } from "../tags/tags-api";
import TextArea from "../components/TextArea";
import MultiSelectField from "../components/MultiSelectField";
import TextField from "../components/TextField";
import { useAuth } from "../auth-context";
import { toast } from "react-toastify";
import StarRatings from 'react-star-ratings';
import { useApi } from "../api-context";

const LEVEL_OPTIONS = [
    { value: '10U', text: '10U' },
    { value: '14U', text: '14U' },
    { value: '19U', text: '19U' }
];

function DrillPage() {
    const api = useApi();
    const [drill, setDrill] = useState<DrillFileDetails>();
    const [tags, setTags] = useState<Tag[]>([]);

    const { id } = useParams();

    const image = `https://hcv-admin-api.azurewebsites.net/scene/image/${id}.jpg?${Math.random()}`;

    const loadTags = () => {
        fetchTags(api)
            .then(tags => setTags(tags.filter(tag => !tag.predefined)))
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load tags');
                }
            });
    };

    const onUpdateDrill = () => {
        updateDrill(api, Number(id), drill!)
            .then(() => toast.success('Successfully updated file'))
            .catch(() => toast.error('Failed to update file'))
    }

    const loadDrill = () => {
        if (!id) {
            return;
        }
        fetchDrill(api, Number(id))
            .then(setDrill)
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load file');
                }
            });
    };

    useEffect(() => {
        loadTags();
        loadDrill();
    }, []);

    if (!drill) {
        return null;
    }

    return (
        <>
            <div className="group-page-header">
                <div className="main-title">
                    Edit file information
                </div>
                <Row gap={8}>
                    <div style={{ flex: 1 }}>
                        <EditableInput
                            value={drill.name}
                            setValue={name => {
                                setDrill({ ...drill, name });
                            }}
                        />
                    </div>
                    <PrimaryButton
                        label="SAVE"
                        style={{ flex: 0, minWidth: '150px' }}
                        onClick={onUpdateDrill}
                    />
                </Row>
            </div>
            <div className="page-container">
                <div className="content-container" style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        <div className="box" style={{ flex: 1, gap: '12px' }}>
                            <TextArea
                                label="Description"
                                placeholder="Enter description"
                                value={drill.description}
                                numberOfLines={8}
                                setValue={description => setDrill({ ...drill, description })}
                            />
                            <TextArea
                                label="Keypoints"
                                placeholder="Enter keypints"
                                value={drill.keypoints}
                                setValue={keypoints => setDrill({ ...drill, keypoints })}
                            />
                            <MultiSelectField
                                label="Tags"
                                placeholder="Select tags"
                                value={drill.tags}
                                options={tags.map(tag => ({ value: tag.id, text: tag.name }))}
                                setValue={tags => setDrill({ ...drill, tags })}
                            />
                            <MultiSelectField
                                label="Levels"
                                placeholder="Select levels"
                                value={drill.levels}
                                options={LEVEL_OPTIONS}
                                setValue={levels => setDrill({ ...drill, levels })}
                            />
                            <TextField
                                label="Defense"
                                placeholder="Enter value"
                                value={drill.defense ? String(drill.defense) : ''}
                                setValue={val => setDrill({ ...drill, defense: Number(val) })}
                                type="number"
                            />
                            <TextField
                                label="Offense"
                                placeholder="Enter value"
                                value={drill.offense ? String(drill.offense) : ''}
                                setValue={val => setDrill({ ...drill, offense: Number(val) })}
                                type="number"
                            />
                            <TextField
                                label="Goalie"
                                placeholder="Enter value"
                                value={drill.goalie ? String(drill.goalie) : ''}
                                setValue={val => setDrill({ ...drill, goalie: Number(val) })}
                                type="number"
                            />
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <img src={image} style={{ maxWidth: '100%' }} />

                            <div className="box" style={{  gap: '4px' }}>
                                <div className="text-field">
                                    <div className="label">Fitness</div>
                                    <StarRatings
                                        rating={drill.stress}
                                        changeRating={(stress) => setDrill({ ...drill, stress })}
                                        numberOfStars={5}
                                        starDimension="40px"
                                        starSpacing="5px"
                                    />
                                </div>
                                <div className="text-field">
                                    <div className="label">Technics</div>
                                    <StarRatings
                                        rating={drill.technics}
                                        changeRating={(technics) => setDrill({ ...drill, technics })}
                                        numberOfStars={5}
                                        starDimension="40px"
                                        starSpacing="5px"
                                    />
                                </div>
                                <div className="text-field">
                                    <div className="label">Tactics</div>
                                    <StarRatings
                                        rating={drill.tactics}
                                        changeRating={(tactics) => setDrill({ ...drill, tactics })}
                                        numberOfStars={5}
                                        starDimension="40px"
                                        starSpacing="5px"
                                    />
                                </div>
                                <div className="text-field">
                                    <div className="label">Fun</div>
                                    <StarRatings
                                        rating={drill.fun}
                                        changeRating={(fun) => setDrill({ ...drill, fun })}
                                        numberOfStars={5}
                                        starDimension="40px"
                                        starSpacing="5px"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default DrillPage;
